import { Flex, Skeleton, Tabs } from '@chakra-ui/react'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AudioPlayer } from '~/components/shared/AudioPlayer'
import { AudioPlayerVariant } from '~/components/shared/componentStyles/AudioPlayer.style'
import TranscriptAudioView from '~/pages/CallAnalytics/Transcription/TranscriptAudioView'
import { CallDetailsOverviewTab } from '~/pages/Calls/CallDetails/CallDetailsOverviewTab'
import { CallDetailsStatsTab } from '~/pages/Calls/CallDetails/CallDetailsStatsTab'
import { CallDetailsTranscriptTab } from '~/pages/Calls/CallDetails/CallDetailsTranscriptTab'
import type { MargoSummaryViewModel } from '~/pages/Calls/CallDetails/MargoSummary/MargoSummary.types'
import { useGetCallTranscript } from '~/queries/callAnalyticsQueries'
import { useGetVoicemail } from '~/queries/voicemailQueries'

import { getTimeDifferenceInMs } from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { CallInstance } from '@mangovoice/types'

import { useCallsViewModelContext } from '../Calls.viewmodel'
import TranscriptProcessingPlaceholder from './TranscriptProcessingPlaceholder'

interface CallDetailsAnalyticsContentProps {
  callInstance: CallInstance
  margoSummary?: MargoSummaryViewModel
  isModal?: boolean
  initialTab?: TCallAnalyticsTab
  onRequestedTimestampChange?: (timestamp: number) => void
  initialTimestamp?: number
  playbackTimestampRef: React.MutableRefObject<number | undefined>
}

export type TCallAnalyticsTab = 'overview' | 'transcript' | 'stats'

// Helper function to get the translated "not available" message
// This is used for comparison in other components
export const getTranscriptSummaryNotAvailable = (t: (key: string) => string) =>
  t('callDetails.summary.notAvailable')

const CallDetailsAnalyticsTab = ({
  children,
  value,
}: {
  children: string
  value: string
}) => {
  return (
    <Tabs.Trigger
      value={value}
      {...textStyles[textStyleKeys.Body2]}
      fontSize="14px"
      color="fg.default"
      borderRadius="0.5rem"
      _hover={{
        bg: 'bg.muted',
      }}
      _selected={{
        bg: 'bg.muted',
      }}
      _focus={{ boxShadow: 'none' }}
      px="1.5rem"
      py="0.75rem"
      flex="1"
      justifyContent="center"
    >
      {children}
    </Tabs.Trigger>
  )
}

const _tabs = ['overview', 'transcript', 'stats'] as const
export const CallDetailsAnalyticsContent = ({
  callInstance,
  margoSummary,
  isModal = false,
  onRequestedTimestampChange,
  initialTimestamp,
  playbackTimestampRef,
}: CallDetailsAnalyticsContentProps) => {
  const { t } = useTranslation()
  const { state, actions } = useCallsViewModelContext()
  const activeTab = state.selectedCallInitialTab ?? 'overview'

  const setActiveTab = useCallback((tab: TCallAnalyticsTab) => {
    actions.appendState({
      selectedCallInitialTab: tab,
    })
  }, [])

  const [requestedTimestamp, setRequestedTimestamp] = useState<number>(
    initialTimestamp ?? 0,
  )

  const [scrollPositions, setScrollPositions] = React.useState<
    Record<TCallAnalyticsTab, number>
  >({} as Record<TCallAnalyticsTab, number>)

  const scrollbarRef = React.useRef<HTMLDivElement | null>(null)

  const [hasBrokenScrollbar, setHasBrokenScrollbar] = React.useState(false)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const [playerPosition, setPlayerPosition] = useState(0)

  const handlePlayStateChange = (isPlaying: boolean) => {
    setIsAudioPlaying(isPlaying)
  }
  const handlePlayerPositionChange = (position: number) => {
    playbackTimestampRef.current = position
    setPlayerPosition(position)
  }
  const handlePlay = (time?: number) => {
    setRequestedTimestamp(time ?? playerPosition)
    onRequestedTimestampChange?.(time ?? playerPosition)
    setIsAudioPlaying(true)
  }
  const handlePause = () => {
    setIsAudioPlaying(false)
  }

  const transcriptQuery = useGetCallTranscript(
    callInstance.pbx.id,
    callInstance.id,
  )

  const voicemailId = callInstance.voicemail_id
  const {
    data: voicemail,
    error: voicemailError,
    isLoading: isVoicemailLoading,
  } = useGetVoicemail({
    voicemailId: voicemailId || 0,
    options: { enabled: !!voicemailId },
  })

  // Add this effect to check for scrollbar
  React.useEffect(() => {
    const checkForScrollbar = () => {
      const userAgent = navigator.userAgent
      const isChrome = userAgent.match(/Chrome/i) !== null

      if (scrollbarRef.current && isChrome) {
        const hasVerticalScrollbar =
          scrollbarRef.current.scrollHeight > scrollbarRef.current.clientHeight
        setHasBrokenScrollbar(hasVerticalScrollbar)
      }
    }

    checkForScrollbar()
    // Add resize listener to check again if window size changes
    window.addEventListener('resize', checkForScrollbar)
    return () => window.removeEventListener('resize', checkForScrollbar)
  }, [transcriptQuery.data]) // Re-run when transcript data changes

  const transcriptSummary =
    transcriptQuery.data?.ConversationCharacteristics.ContactSummary
      ?.AutoGenerated.OverallSummary.Content

  const overallCustomerScore =
    transcriptQuery.data?.ConversationCharacteristics.Sentiment.OverallSentiment
      .CUSTOMER
  const overallAgentScore =
    transcriptQuery.data?.ConversationCharacteristics.Sentiment.OverallSentiment
      .AGENT

  const handleTabsChange = (details: { value: string | null }) => {
    // Save the current scroll position
    if (scrollbarRef.current) {
      const currentScrollPosition = scrollbarRef.current.scrollTop

      setScrollPositions((prev) => ({
        ...prev,
        [activeTab]: currentScrollPosition,
      }))
    }

    // Update the active tab
    if (details.value) {
      setActiveTab(details.value as TCallAnalyticsTab)
    }
  }

  const answerTimestampInMs =
    callInstance && !callInstance.is_missed && callInstance.first_answered_at
      ? getTimeDifferenceInMs(
          new Date(callInstance.first_answered_at),
          new Date(callInstance.started_at),
        )
      : undefined

  // Restore scroll position when the active tab changes
  React.useEffect(() => {
    if (scrollbarRef.current) {
      const savedScrollPosition = scrollPositions[activeTab] || 0
      scrollbarRef.current.scrollTop = savedScrollPosition
    }
  }, [activeTab, scrollPositions])

  const panelPadding = hasBrokenScrollbar
    ? '1.25rem 0.65rem 1.25rem 1.25rem'
    : '1.25rem'
  return transcriptQuery.isLoading ? (
    <Flex p="1.5rem" w="full" flexDir="column" gap="1.25rem">
      <Skeleton h="5.625rem" />
      <Flex w="full" h="9.625rem" gap="1.25rem">
        <Skeleton flexGrow="1" />
        <Skeleton flexGrow="1" />
      </Flex>
      <Skeleton h="10.375rem" />
      <Skeleton h="7.75rem" />
    </Flex>
  ) : (
    <Flex
      flexDirection="column"
      flex={isModal ? 'none' : '1'}
      overflow={isModal ? 'visible' : 'hidden'}
    >
      <Flex
        flex="0 1 auto"
        flexDirection="column"
        padding="1.25rem"
        paddingBottom="0"
        gap="1.25rem"
      >
        {!callInstance.recording_url ? (
          <div>{t('callDetails.noRecordingAvailable')}</div>
        ) : // Conditional Rendering for Transcript Processing
        callInstance?.transcription_status === 'COMPLETED' ? (
          <TranscriptAudioView
            audioUri={callInstance.recording_url}
            callTranscript={transcriptQuery.data}
            onAudioPlayerPositionChange={handlePlayerPositionChange}
            onAudioPlayStateChange={handlePlayStateChange}
            requestedTimestamp={requestedTimestamp}
            requestedIsPlaying={isAudioPlaying}
          />
        ) : (
          <AudioPlayer
            variant={AudioPlayerVariant.Recording}
            audioUri={callInstance.recording_url}
            showJumpToAnswerButton={false}
            showInfoButton={false}
            showInlineInfoButton={true}
            requestedTimestamp={requestedTimestamp}
            requestedIsPlaying={isAudioPlaying}
            onValueChanged={handlePlayerPositionChange}
            onPlayStateChange={handlePlayStateChange}
          />
        )}

        <Tabs.Root
          value={activeTab}
          onValueChange={handleTabsChange}
          marginLeft="-1.25rem"
          marginRight="-1.25rem"
          variant="plain"
        >
          <Tabs.List
            display="flex"
            justifyContent="space-evenly"
            gap="0.25rem"
            borderBottom="2px solid"
            height="2.875rem"
            borderColor="border.muted"
            width="full"
            padding="0 1.25rem"
            paddingBottom="0.75rem"
            onKeyDown={(event) => {
              const keysToPrevent = ['ArrowLeft', 'ArrowRight']
              if (keysToPrevent.includes(event.key)) {
                event.preventDefault()
              }
            }}
          >
            <CallDetailsAnalyticsTab value="overview">
              {t('callDetails.tabs.overview')}
            </CallDetailsAnalyticsTab>
            <CallDetailsAnalyticsTab value="transcripts">
              {t('callDetails.tabs.transcripts')}
            </CallDetailsAnalyticsTab>
            <CallDetailsAnalyticsTab value="stats">
              {t('callDetails.tabs.stats')}
            </CallDetailsAnalyticsTab>
          </Tabs.List>
        </Tabs.Root>
      </Flex>
      <Flex
        flex={isModal ? 'none' : '1'}
        flexDir={'column'}
        overflow={isModal ? 'visible' : 'scroll'}
        minHeight="0"
        ref={scrollbarRef}
      >
        <Tabs.Root value={activeTab}>
          <Tabs.ContentGroup>
            {/* Overview Tab Panel */}
            <Tabs.Content value="overview" padding={panelPadding}>
              <CallDetailsOverviewTab
                callInstance={callInstance}
                margoSummary={margoSummary}
                answerTimestampInMs={answerTimestampInMs}
                transcriptSummary={
                  transcriptSummary ?? t('callDetails.summary.notAvailable')
                }
                overallCustomerScore={overallCustomerScore}
                overallAgentScore={overallAgentScore}
                isLoadingTranscripts={transcriptQuery.isLoading}
                voicemail={voicemail}
                voicemailError={voicemailError ? voicemailError : undefined}
                isVoicemailLoading={isVoicemailLoading}
              />
            </Tabs.Content>
            {/* Transcript Tab Panel */}
            <Tabs.Content value="transcripts" padding={panelPadding}>
              {callInstance?.transcription_status !== 'COMPLETED' ? (
                <TranscriptProcessingPlaceholder isInTab={true} />
              ) : (
                <CallDetailsTranscriptTab
                  transcript={transcriptQuery.data}
                  isLoadingTranscripts={transcriptQuery.isLoading}
                  playerPosition={playerPosition}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  isAudioPlaying={isAudioPlaying}
                />
              )}
            </Tabs.Content>
            {/* Stats Tab Panel */}
            <Tabs.Content value="stats" padding={panelPadding}>
              {callInstance?.transcription_status !== 'COMPLETED' ? (
                <TranscriptProcessingPlaceholder isInTab={true} />
              ) : (
                <CallDetailsStatsTab
                  transcript={transcriptQuery.data}
                  isLoadingTranscripts={transcriptQuery.isLoading}
                />
              )}
            </Tabs.Content>
          </Tabs.ContentGroup>
        </Tabs.Root>
      </Flex>
    </Flex>
  )
}
